export const sanitizeInput = (input: string, maxLength: number = 255): string => {
  if (!input) return '';

  return input
    .trim()
    .slice(0, maxLength)
    .replace(/[<>]/g, '');
};

export const sanitizeSearchInput = (input: string): string => {
  return sanitizeInput(input, 100);
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validateUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const validateUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

export const validateDateString = (date: string): boolean => {
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
};

export const validateNumber = (value: string, min?: number, max?: number): boolean => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  if (min !== undefined && num < min) return false;
  if (max !== undefined && num > max) return false;
  return true;
};

export const validateFieldName = (fieldName: string, allowedFields: string[]): boolean => {
  return allowedFields.includes(fieldName);
};

export const sanitizeHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

export const validateSortField = (field: string, allowedFields: string[]): boolean => {
  return validateFieldName(field, allowedFields);
};

export const validateFilterValue = (value: string): string => {
  return sanitizeInput(value, 255);
};

export const validatePaginationParams = (
  page: number,
  pageSize: number
): { valid: boolean; page: number; pageSize: number } => {
  const validPage = Math.max(1, Math.floor(page));
  const validPageSize = Math.min(1000, Math.max(1, Math.floor(pageSize)));

  return {
    valid: validPage === page && validPageSize === pageSize,
    page: validPage,
    pageSize: validPageSize,
  };
};

export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 255);
};

export const validateAlphanumeric = (input: string): boolean => {
  return /^[a-zA-Z0-9]+$/.test(input);
};

export const validateAlphanumericWithSpaces = (input: string): boolean => {
  return /^[a-zA-Z0-9\s]+$/.test(input);
};

export const preventSqlInjection = (input: string): string => {
  return input
    .replace(/['";\\]/g, '')
    .trim();
};

// Normalize any date value from the API to a plain "YYYY-MM-DD" string.
// pg returns DATE columns as "YYYY-MM-DD" strings after our type-parser fix,
// but any legacy data stored as a timestamp will come through as ISO format.
export const toDateOnly = (val: string | Date | null | undefined): string => {
  if (!val) return '';
  const s = typeof val === 'string' ? val : (val as Date).toISOString();
  return s.substring(0, 10);
};
